import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { track } from "../../lib/analytics";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { currentRegion } from "../../config/regions";
import { storedUnitSystem } from "../../lib/bodyUnits";
import ScopePicker, { customPeriodError, previewScopePayload, scopePayload } from "./ScopePicker";

const FORMATS = [
  { id: "pdf", labelKey: "history.format.pdf" },
  { id: "fhir_ips", labelKey: "history.format.fhir" },
  { id: "zip", labelKey: "history.format.zip" },
];

const EXPIRY = ["1h", "24h", "7d", "30d"];

const INITIAL_SCOPE = {
  includeProfile: true,
  includeIdentity: true,
  includeAi: false,
  attachDocuments: true,
  note: "",
  excludedIds: [],
  choosing: false,
  periodPreset: "3y",
  periodFrom: "",
  periodTo: "",
  includeUndated: true,
};

function filenameFromDisposition(header) {
  if (!header) return "medical-history";
  const starred = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (starred) {
    try {
      return decodeURIComponent(starred[1]);
    } catch {
      return "medical-history";
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : "medical-history";
}

async function errorPayload(error) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  return data && typeof data === "object" ? data : null;
}

const SCOPE_ERROR_REASONS = [
  "empty",
  "outside_period",
  "not_found",
  "invalid_period",
  "note_too_long",
  "ambiguous_shape",
];

function scopeErrorText(t, payload) {
  const reason = payload?.error?.reason;
  if (SCOPE_ERROR_REASONS.includes(reason)) return t(`history.scopeError.${reason}`);
  return t("history.scopeError.empty");
}

function formatBytes(bytes) {
  const mb = (Number(bytes) || 0) / (1024 * 1024);
  if (mb < 0.1) return "<0.1 MB";
  const rounded = mb >= 10 ? String(Math.round(mb)) : mb.toFixed(1);
  return `${rounded} MB`;
}

function previewText(t, data) {
  if (!data) return t("history.scopePreviewLoading");
  return t("history.scopePreview")
    .replace("{records}", String(data.record_count ?? 0))
    .replace("{documents}", String(data.document_count ?? 0))
    .replace("{size}", formatBytes(data.document_bytes))
    .replace("{consultations}", String(data.consultation_count ?? 0));
}

function formatOverLimit(preview, tab, format) {
  const limits = preview && preview.over_size_limit;
  if (!limits || typeof limits !== "object") return false;
  if (tab === "link") return Boolean(limits.pdf);
  if (format === "pdf" || format === "zip") return Boolean(limits[format]);
  return false;
}

function scopeFlags(scope) {
  const preset = scope.periodPreset || "3y";
  return {
    include_profile: Boolean(scope.includeProfile),
    include_identity: Boolean(scope.includeIdentity),
    include_ai: Boolean(scope.includeAi),
    attach_documents: Boolean(scope.attachDocuments),
    has_period: preset !== "all",
    period_preset: preset,
    include_undated: preset === "all" ? true : scope.includeUndated !== false,
    has_note: Boolean((scope.note || "").trim()),
  };
}

export default function DoctorPacketDialog({
  open,
  onOpenChange,
  subjectId,
  records,
  recordsTotal,
  hasExportIdentity,
  displayName,
  initialTab,
  onCreated,
}) {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState(initialTab === "link" ? "link" : "download");
  const [scope, setScope] = useState(INITIAL_SCOPE);
  const [preview, setPreview] = useState(null);
  const [format, setFormat] = useState("pdf");
  const [legalName, setLegalName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [saveIdentity, setSaveIdentity] = useState(false);
  const [useSaved, setUseSaved] = useState(false);
  const [expiresIn, setExpiresIn] = useState("24h");
  const [requireCode, setRequireCode] = useState(true);
  const [notify, setNotify] = useState(true);
  const [consent, setConsent] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [working, setWorking] = useState(false);
  const [created, setCreated] = useState(null);
  const [identityOmitted, setIdentityOmitted] = useState(false);
  const [periods, setPeriods] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab === "link" ? "link" : "download");
    setScope(INITIAL_SCOPE);
    setPreview(null);
    setFormat("pdf");
    setLegalName("");
    setDateOfBirth("");
    setSaveIdentity(false);
    setUseSaved(Boolean(hasExportIdentity));
    setExpiresIn("24h");
    setRequireCode(true);
    setNotify(true);
    setAccepted(false);
    setWorking(false);
    setCreated(null);
    setIdentityOmitted(false);
    setPeriods(null);
  }, [open, initialTab, hasExportIdentity]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/history/share-consent`, { params: { locale: language } })
      .then((response) => {
        if (!cancelled) setConsent(response.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setConsent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, language]);

  useEffect(() => {
    if (!open || !subjectId) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/subjects/${subjectId}/exports/periods`, {
        params: { include_ai_opinions: scope.includeAi },
      })
      .then((response) => {
        if (!cancelled) setPeriods(response.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setPeriods(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, subjectId, scope.includeAi]);

  const previewKey = customPeriodError(scope)
    ? ""
    : JSON.stringify(previewScopePayload(scope));

  useEffect(() => {
    if (!open || !subjectId) return undefined;
    if (!previewKey) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const body = JSON.parse(previewKey);
    const timer = setTimeout(() => {
      axios
        .post(`${API}/subjects/${subjectId}/exports/preview`, { scope: body })
        .then((response) => {
          if (!cancelled) setPreview(response.data?.data || null);
        })
        .catch(() => {
          if (!cancelled) setPreview(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, subjectId, previewKey]);

  const periodBlocked = Boolean(customPeriodError(scope));
  const sizeWarning = formatOverLimit(preview, tab, format);
  const person = displayName || t("history.sharePersonFallback");
  const consentText = (consent?.text || "").split("{name}").join(person);
  const showIdentityForm = scope.includeIdentity && !useSaved;

  const copy = async (value, successKey = "history.shareCopied") => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t(successKey));
    } catch {
      toast.error(t("history.shareError"));
    }
  };

  const download = async () => {
    if (!subjectId || customPeriodError(scope)) return;
    setWorking(true);
    const body = {
      format,
      scope: scopePayload(scope),
      language,
      unit_system: storedUnitSystem(currentRegion),
    };
    if (scope.includeIdentity && !useSaved) {
      body.legal_name = legalName.trim();
      body.date_of_birth = (dateOfBirth || "").trim();
      body.save_identity = saveIdentity;
    }
    try {
      const response = await axios.post(`${API}/subjects/${subjectId}/exports`, body, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filenameFromDisposition(response.headers?.["content-disposition"]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      track("history_exported", { format, ...scopeFlags(scope) });
      toast.success(t("history.exportDone"));
      onOpenChange(false);
    } catch (error) {
      const status = error?.response?.status;
      const payload = await errorPayload(error);
      const code = payload?.error?.code;
      if (status === 413 || code === "export_too_large") toast.error(t("history.exportTooLarge"));
      else if (code === "invalid_scope") toast.error(scopeErrorText(t, payload));
      else if (status === 422) toast.error(t("history.exportIdentityInvalid"));
      else toast.error(t("history.exportError"));
    } finally {
      setWorking(false);
    }
  };

  const share = async () => {
    if (!subjectId || customPeriodError(scope)) return;
    const entered = legalName.trim() || (dateOfBirth || "").trim();
    const needsEntry = scope.includeIdentity && !useSaved;
    if (needsEntry && !entered) {
      setScope((current) => ({ ...current, includeIdentity: false }));
      setIdentityOmitted(true);
      return;
    }
    if (!accepted || !consent?.text_version) {
      toast.error(t("history.shareConsentRequired"));
      return;
    }
    setWorking(true);
    const body = {
      scope: scopePayload(scope),
      expires_in: expiresIn,
      require_code: requireCode,
      notify_on_open: notify,
      consent_text_version: consent.text_version,
      consent_locale: consent.locale || language,
      unit_system: storedUnitSystem(currentRegion),
    };
    if (needsEntry) {
      body.legal_name = legalName.trim();
      body.date_of_birth = (dateOfBirth || "").trim();
      body.save_identity = true;
    }
    try {
      const response = await axios.post(`${API}/subjects/${subjectId}/shares`, body);
      setCreated(response.data?.data || null);
      track("history_share_created", {
        expires_in: expiresIn,
        require_code: requireCode,
        ...scopeFlags(scope),
      });
      if (onCreated) onCreated();
    } catch (error) {
      const payload = error?.response?.data;
      const code = payload?.error?.code;
      if (code === "share_limit") toast.error(t("history.shareLimit"));
      else if (code === "share_consent_required") toast.error(t("history.shareConsentRequired"));
      else if (code === "invalid_scope") toast.error(scopeErrorText(t, payload));
      else toast.error(t("history.shareError"));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="packet-dialog">
        <DialogHeader>
          <DialogTitle>{t("history.packetTitle")}</DialogTitle>
          <DialogDescription>{t("history.packetDescription")}</DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-3" data-testid="share-created">
            <p className="text-sm">{t("history.shareCodeOnce")}</p>
            <div>
              <p className="text-xs text-muted-foreground">{t("history.shareUrl")}</p>
              <p className="text-sm break-all" data-testid="share-qr-payload">
                {created.qr_payload || created.url}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-1"
                onClick={() => copy(created.url)}
              >
                {t("history.shareCopy")}
              </Button>
            </div>
            {created.access_code ? (
              <div>
                <p className="text-xs text-muted-foreground">{t("history.shareCode")}</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-3xl tracking-widest" data-testid="share-access-code">
                    {created.access_code}
                  </p>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    aria-label={t("history.shareCopyCode")}
                    data-testid="share-copy-code"
                    onClick={() => copy(created.access_code, "history.shareCodeCopied")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t("history.shareExpiresAt").replace("{date}", (created.expires_at || "").slice(0, 16))}
            </p>
            <Button type="button" onClick={() => onOpenChange(false)}>
              {t("history.shareDone")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <ScopePicker
              records={records}
              recordsTotal={recordsTotal}
              scope={scope}
              onChange={setScope}
              previewLine={previewText(t, preview)}
              periods={periods}
            />

            <div className="space-y-3">
              <p className="text-sm font-medium">{t("history.packetStepSend")}</p>
              <div className="flex gap-2" role="tablist">
                <Button
                  type="button"
                  size="sm"
                  variant={tab === "download" ? "default" : "outline"}
                  onClick={() => setTab("download")}
                  data-testid="packet-tab-download"
                  role="tab"
                  aria-selected={tab === "download"}
                >
                  {t("history.downloadPacket")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={tab === "link" ? "default" : "outline"}
                  onClick={() => setTab("link")}
                  data-testid="packet-tab-link"
                  role="tab"
                  aria-selected={tab === "link"}
                >
                  {t("history.shareWithDoctor")}
                </Button>
              </div>

              {tab === "download" ? (
                <div className="space-y-3">
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">{t("history.exportFormat")}</legend>
                    {FORMATS.map((item) => (
                      <label key={item.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="export-format"
                          value={item.id}
                          checked={format === item.id}
                          onChange={() => setFormat(item.id)}
                          className="accent-primary"
                          data-testid={`export-format-${item.id}`}
                        />
                        <span>{t(item.labelKey)}</span>
                      </label>
                    ))}
                  </fieldset>
                  {sizeWarning ? (
                    <p className="text-sm text-destructive" data-testid="export-size-warning" role="status">
                      {t("history.exportTooLarge")}
                    </p>
                  ) : null}
                  {showIdentityForm ? (
                    <IdentityFields
                      t={t}
                      legalName={legalName}
                      dateOfBirth={dateOfBirth}
                      saveIdentity={saveIdentity}
                      onName={setLegalName}
                      onDob={setDateOfBirth}
                      onSave={setSaveIdentity}
                      saveLabel={t("history.packetSaveIdentity")}
                      help={t("history.exportIdentityHelp")}
                    />
                  ) : scope.includeIdentity ? (
                    <div className="space-y-2">
                      <p className="text-sm">{t("history.exportUseSaved")}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUseSaved(false)}
                        data-testid="export-change-identity"
                      >
                        {t("history.exportChangeIdentity")}
                      </Button>
                    </div>
                  ) : null}
                  <Button onClick={download} disabled={working || !subjectId || periodBlocked} data-testid="export-submit">
                    {working ? t("history.exportWorking") : t("history.exportDownload")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {sizeWarning ? (
                    <p className="text-sm text-destructive" data-testid="export-size-warning" role="status">
                      {t("history.exportTooLarge")}
                    </p>
                  ) : null}
                  <fieldset className="space-y-1">
                    <legend className="text-sm font-medium">{t("history.shareExpires")}</legend>
                    {EXPIRY.map((item) => (
                      <label key={item} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="share-expires"
                          checked={expiresIn === item}
                          onChange={() => setExpiresIn(item)}
                          className="accent-primary"
                          data-testid={`share-expires-${item}`}
                        />
                        <span>{t(`history.shareExpires.${item}`)}</span>
                      </label>
                    ))}
                  </fieldset>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={requireCode}
                      onChange={(event) => setRequireCode(event.target.checked)}
                      data-testid="share-require-code"
                    />
                    <span>{t("history.shareRequireCode")}</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={notify}
                      onChange={(event) => setNotify(event.target.checked)}
                      data-testid="share-notify"
                    />
                    <span>{t("history.shareNotify")}</span>
                  </label>
                  {showIdentityForm ? (
                    <IdentityFields
                      t={t}
                      legalName={legalName}
                      dateOfBirth={dateOfBirth}
                      saveIdentity
                      onName={setLegalName}
                      onDob={setDateOfBirth}
                      onSave={setSaveIdentity}
                      saveLabel={t("history.packetSaveIdentity")}
                      help={t("history.packetSaveRequired")}
                      saveRequired
                    />
                  ) : scope.includeIdentity ? (
                    <div className="space-y-2">
                      <p className="text-sm">{t("history.exportUseSaved")}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUseSaved(false)}
                        data-testid="export-change-identity"
                      >
                        {t("history.exportChangeIdentity")}
                      </Button>
                    </div>
                  ) : null}
                  {identityOmitted ? (
                    <p className="text-sm" data-testid="packet-identity-omitted">
                      {t("history.packetIdentityOmitted")}
                    </p>
                  ) : null}
                  <div className="rounded-md border border-border p-3 text-sm" data-testid="share-consent-text">
                    {consentText || t("history.loadError")}
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={accepted}
                      onChange={(event) => setAccepted(event.target.checked)}
                      data-testid="share-consent-accept"
                    />
                    <span>{t("history.shareConsentAccept")}</span>
                  </label>
                  <Button
                    type="button"
                    onClick={share}
                    disabled={working || !accepted || periodBlocked}
                    data-testid="share-create"
                  >
                    {working ? t("history.shareWorking") : t("history.shareCreate")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IdentityFields({
  t,
  legalName,
  dateOfBirth,
  saveIdentity,
  onName,
  onDob,
  onSave,
  saveLabel,
  help,
  saveRequired = false,
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t("history.exportIdentityTitle")}</p>
      <p
        className="text-xs text-muted-foreground"
        data-testid={saveRequired ? "packet-save-required" : undefined}
      >
        {help}
      </p>
      <div className="space-y-1">
        <Label htmlFor="export-legal-name">{t("history.exportLegalName")}</Label>
        <Input
          id="export-legal-name"
          value={legalName}
          maxLength={200}
          onChange={(event) => onName(event.target.value)}
          data-testid="export-legal-name"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="export-dob">{t("history.exportDateOfBirth")}</Label>
        <Input
          id="export-dob"
          type="date"
          value={dateOfBirth}
          onChange={(event) => onDob(event.target.value)}
          data-testid="export-dob"
        />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={saveRequired || saveIdentity}
          disabled={saveRequired}
          onChange={(event) => onSave(event.target.checked)}
          data-testid="export-save-identity"
        />
        <span>{saveLabel}</span>
      </label>
    </div>
  );
}
